import React, { useState, useEffect } from 'react';
import { FiPlus, FiFolder, FiTrash2, FiX, FiMessageCircle, FiDownload, FiFile, FiUpload, FiFileText, FiCheckSquare, FiEdit2, FiUsers, FiShare2, FiChevronDown, FiChevronUp, FiChevronRight, FiBarChart2, FiBookOpen } from 'react-icons/fi';
import SearchResults from '../components/SearchResults';
import Chatbot from '../components/Chatbot';
import LitReviewModal from '../components/LitReviewModal';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import { isGuideSeen, markGuideSeen } from '../utils/onboarding';
import './Collections.css';


const Collections = ({ onBack, onCitationSearch, onStartGrantAgent, onStartPaperAgent, initialCollectionId, onMounted, user }) => {
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showLitReviewModal, setShowLitReviewModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDesc, setNewCollectionDesc] = useState('');
  const [contextDocuments, setContextDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [activeTab, setActiveTab] = useState('collection-analysis'); // 'papers', 'context-docs', 'notes', 'actions', 'collection-analysis', 'assistant'
  const [notes, setNotes] = useState([]);
  const [actionableItems, setActionableItems] = useState([]);
  const [showCreateNoteModal, setShowCreateNoteModal] = useState(false);
  const [showCreateActionModal, setShowCreateActionModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editingActionItem, setEditingActionItem] = useState(null);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionDescription, setNewActionDescription] = useState('');
  const [newActionPriority, setNewActionPriority] = useState('medium');
  const [newActionStartDate, setNewActionStartDate] = useState('');
  const [newActionDueDate, setNewActionDueDate] = useState('');
  const [newActionAssignee, setNewActionAssignee] = useState(null);
  const [teams, setTeams] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [collectionTeamId, setCollectionTeamId] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  // Action items filters and sorting
  const [actionFilterCreatedBy, setActionFilterCreatedBy] = useState('all');
  const [actionFilterAssignedTo, setActionFilterAssignedTo] = useState('all');
  const [actionFilterStatus, setActionFilterStatus] = useState('all');
  const [actionSortBy, setActionSortBy] = useState('updated'); // 'updated', 'start_date', 'due_date'
  const [actionSortOrder, setActionSortOrder] = useState('desc'); // 'asc', 'desc'
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Analysis panels
  const [expandedPanels, setExpandedPanels] = useState({
    ideasSection: true,  // ideas expanded by default — the main value
  });
  // Author import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importQuery, setImportQuery] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');

  // Research ideas state
  const [ideas, setIdeas] = useState([]);
  const [ideasContext, setIdeasContext] = useState(null); // { collections_count, proposals_count, collection_names, proposal_titles }
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [expandedPapers, setExpandedPapers] = useState({}); // key: `${ideaIdx}_${paperIdx}`

  // Twin depth stats
  const [twinStats, setTwinStats] = useState(null);

  // Onboarding guide
  const [showGuide, setShowGuide] = useState(() => !isGuideSeen(user?.id, 'ideas_guide'));


  // Paper recommendations
  const [recommendations, setRecommendations] = useState(null);
  const [recsLoading, setRecsLoading] = useState(false);


  // Inline rename state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const perPage = 25;

  useEffect(() => {
    fetchCollections();
    fetchActiveCollection();
    fetchTeams();
    fetchTwinStats();
  }, []);

  useEffect(() => {
    if (activeCollection) {
      // Check if collection is shared with a team
      fetchCollectionDetails();
    }
  }, [activeCollection]);

  useEffect(() => {
    if (activeCollection) {
      fetchCollectionWorks(activeCollection.id, 1);
      fetchContextDocuments(activeCollection.id);
      fetchNotes(activeCollection.id);
      fetchActionableItems(activeCollection.id);
      setCollectionTeamId(activeCollection.team_id || null);
    }
  }, [activeCollection]);

  useEffect(() => {
    // Default to collection-analysis tab when collection has papers, else papers tab
    if (activeCollection) {
      setActiveTab('collection-analysis');
      setIdeas([]);
      setIdeasContext(null);
      setExpandedPapers({});
    }
  }, [activeCollection?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Auto-load cached ideas when user switches to the analysis tab
    if (activeTab === 'collection-analysis' && activeCollection) {
      if (ideas.length === 0 && !ideasLoading) {
        fetchIdeas(activeCollection.id, false);
      }
    }
  }, [activeTab, activeCollection?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCollections = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/collections'), {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch collections');
      const data = await response.json();
      setCollections(data);
      // Auto-activate seed collection if provided on mount
      if (initialCollectionId) {
        const seeded = data.find((c) => c.id === initialCollectionId);
        if (seeded) {
          setActiveCollection(seeded);
        }
        if (onMounted) onMounted();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const logIdeaInteraction = (collectionId, idea, action) => {
    // Fire-and-forget — never blocks the user action
    fetch(getApiEndpoint(`/api/collections/${collectionId}/ideas/interaction`), {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idea_title: idea.title,
        action,
        expertise_depth: idea.expertise_depth ?? null,
        novelty_count: idea.novelty_count ?? null,
      }),
    }).catch(() => {});
  };



  const fetchRecommendations = async (collectionId) => {
    setRecsLoading(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/collections/${collectionId}/recommendations`), {
        headers: getAuthHeaders(),
      });
      if (res.ok) setRecommendations(await res.json());
    } catch (_) {}
    setRecsLoading(false);
  };

  const addRecommendedPaper = async (collectionId, workId) => {
    try {
      await fetch(getApiEndpoint(`/api/collections/${collectionId}/works`), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ work_id: workId }),
      });
      // Remove from recommendations list
      setRecommendations(prev => prev ? {
        ...prev,
        recommendations: prev.recommendations.filter(r => r.id !== workId),
      } : prev);
    } catch (_) {}
  };


  const fetchTwinStats = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/collections/twin-stats'), {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setTwinStats(data);
      }
    } catch (err) {
      // non-critical, ignore
    }
  };


  const fetchTeams = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/teams'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  };

  const fetchCollectionDetails = async () => {
    if (!activeCollection) return;
    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${activeCollection.id}`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCollectionTeamId(data.team_id || null);
        // Fetch team members if collection is shared
        if (data.team_id) {
          fetchTeamMembers(data.team_id);
        } else {
          setTeamMembers([]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch collection details:', err);
    }
  };

  const fetchTeamMembers = async (teamId) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${teamId}`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.members || []);
      }
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    }
  };

  const handleShareWithTeam = async (teamId) => {
    if (!activeCollection) return;

    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${teamId}/collections/${activeCollection.id}`), {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to share collection');
      }

      setCollectionTeamId(teamId);
      // Update activeCollection to reflect the team_id
      if (activeCollection) {
        setActiveCollection({ ...activeCollection, team_id: teamId });
      }
      setShowShareModal(false);
      fetchCollections();
      alert('Collection shared with team successfully!');
    } catch (err) {
      alert(`Failed to share collection: ${err.message}`);
    }
  };

  const handleUnshareFromTeam = async () => {
    if (!activeCollection || !collectionTeamId) return;

    if (!window.confirm('Are you sure you want to unshare this collection from the team?')) {
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${collectionTeamId}/collections/${activeCollection.id}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to unshare collection');
      }

      setCollectionTeamId(null);
      // Update activeCollection to reflect the team_id removal
      if (activeCollection) {
        setActiveCollection({ ...activeCollection, team_id: null });
      }
      fetchCollections();
      alert('Collection unshared from team successfully!');
    } catch (err) {
      alert(`Failed to unshare collection: ${err.message}`);
    }
  };

  const fetchActiveCollection = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/collections/active'), {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch active collection');
      const data = await response.json();
      if (data) {
        setActiveCollection(data);
      }
    } catch (err) {
      // No active collection is fine
    }
  };

  const fetchCollectionWorks = async (collectionId, page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        getApiEndpoint(`/api/collections/${collectionId}/works?page=${page}&per_page=${perPage}`),
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error('Failed to fetch collection works');
      const data = await response.json();
      setResults(data);
      setCurrentPage(page);
    } catch (err) {
      setError(err.message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchIdeas = async (collectionId, forceRefresh = false) => {
    if (!collectionId) return;
    setIdeasLoading(true);
    try {
      const url = getApiEndpoint(`/api/collections/${collectionId}/ideas`) +
        (forceRefresh ? '?refresh=true' : '');
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch ideas');
      const data = await response.json();
      // Handle both wrapped { ideas, context_used } and legacy array format
      const ideasArray = Array.isArray(data) ? data : (data.ideas || []);
      const ctx = Array.isArray(data) ? null : (data.context_used || null);
      setIdeas(ideasArray);
      setIdeasContext(ctx);
      setExpandedPapers({});
      // Refresh twin stats after new ideas are generated
      if (forceRefresh) fetchTwinStats();
    } catch (err) {
      console.error('Error fetching ideas:', err);
      if (forceRefresh) alert(`Failed to generate ideas: ${err.message}`);
    } finally {
      setIdeasLoading(false);
    }
  };

  const importAuthorPapers = async () => {
    if (!importQuery.trim()) return;
    setImportLoading(true);
    setImportError('');
    try {
      const response = await fetch(getApiEndpoint('/api/collections/import-author'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: importQuery.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Import failed');
      // Refresh collections list and navigate to the new one
      await fetchCollections();
      setShowImportModal(false);
      setImportQuery('');
      // Find and activate the new collection
      const newColl = { id: data.collection_id, name: `${data.author_name} — Publications` };
      setActiveCollection(newColl);
      setActiveTab('collection-analysis');
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImportLoading(false);
    }
  };

  const togglePaperChip = (ideaIdx, paperIdx) => {
    const key = `${ideaIdx}_${paperIdx}`;
    setExpandedPapers(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePanel = (panelKey) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panelKey]: !prev[panelKey]
    }));
  };

  const handleCreateCollection = async (e) => {
    e.preventDefault();
    if (!newCollectionName.trim()) return;

    try {
      const response = await fetch(getApiEndpoint('/api/collections'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          name: newCollectionName,
          description: newCollectionDesc || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create collection');
      }

      const data = await response.json();
      setCollections([data, ...collections]);
      setActiveCollection(data);
      setShowCreateModal(false);
      setNewCollectionName('');
      setNewCollectionDesc('');
      fetchCollectionWorks(data.id, 1);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSelectCollection = async (collection) => {
    // Set as active
    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collection.id}/set-active`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to set active collection');
      setActiveCollection(collection);
      fetchCollectionWorks(collection.id, 1);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCollection = async (collectionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this collection? This action cannot be undone.')) return;

    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete collection');
      }

      // Remove from list
    setCollections(collections.filter(c => c.id !== collectionId));
      
      // If it was the active collection, clear it
    if (activeCollection && activeCollection.id === collectionId) {
      setActiveCollection(null);
      setResults(null);
      }
    } catch (err) {
      alert(`Failed to delete collection: ${err.message}`);
    }
  };

  const handleRenameCollection = async () => {
    const trimmed = editedName.trim();
    if (!trimmed || !activeCollection || trimmed === activeCollection.name) {
      setIsEditingName(false);
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${activeCollection.id}`), {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to rename collection');
      }

      const updated = await response.json();
      setActiveCollection(prev => ({ ...prev, name: updated.name }));
      setCollections(prev => prev.map(c => c.id === updated.id ? { ...c, name: updated.name } : c));
    } catch (err) {
      alert(`Failed to rename collection: ${err.message}`);
    } finally {
      setIsEditingName(false);
    }
  };

  const handleRemoveFromCollection = async (work) => {
    if (!activeCollection) return;
    if (!window.confirm(`Remove "${work.title || 'this paper'}" from the collection?`)) return;

    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${activeCollection.id}/works/${encodeURIComponent(work.id)}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to remove work');
      }

      // Refresh the papers list
      fetchCollectionWorks(activeCollection.id, currentPage);
      // Update item count in sidebar
      setCollections(prev => prev.map(c =>
        c.id === activeCollection.id ? { ...c, item_count: Math.max(0, (c.item_count || 1) - 1) } : c
      ));
    } catch (err) {
      alert(`Failed to remove paper: ${err.message}`);
    }
  };

  const handlePageChange = (newPage) => {
    if (activeCollection) {
      fetchCollectionWorks(activeCollection.id, newPage);
    }
  };

  const fetchContextDocuments = async (collectionId) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/context-documents`), {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch context documents');
      const data = await response.json();
      setContextDocuments(data);
    } catch (err) {
      console.error('Error fetching context documents:', err);
      setContextDocuments([]);
    }
  };

  const handleUploadContextDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'text/plain', 'text/markdown'];
    const allowedExtensions = ['.pdf', '.txt', '.md'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      alert('Please select a PDF, TXT, or MD file');
      return;
    }

    if (!activeCollection) return;

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);

      const response = await fetch(getApiEndpoint(`/api/collections/${activeCollection.id}/context-documents`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload document');
      }

      await fetchContextDocuments(activeCollection.id);
      alert('Context document uploaded successfully!');
    } catch (err) {
      alert(`Failed to upload document: ${err.message}`);
    } finally {
      setUploadingDoc(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleDownloadContextDocument = async (doc) => {
    try {
      const response = await fetch(
        getApiEndpoint(`/api/collections/${activeCollection.id}/context-documents/${doc.id}/download`),
        { headers: getAuthHeaders() }
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Download failed');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Failed to download document: ${err.message}`);
    }
  };

  const handleDeleteContextDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this context document?')) return;
    if (!activeCollection) return;

    try {
      const response = await fetch(
        getApiEndpoint(`/api/collections/${activeCollection.id}/context-documents/${documentId}`),
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete document');
      }

      await fetchContextDocuments(activeCollection.id);
    } catch (err) {
      alert(`Failed to delete document: ${err.message}`);
    }
  };

  const fetchNotes = async (collectionId) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/notes`), {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch notes');
      const data = await response.json();
      setNotes(data);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
      setNotes([]);
    }
  };

  const fetchActionableItems = async (collectionId) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/actionable-items`), {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch actionable items');
      const data = await response.json();
      setActionableItems(data);
    } catch (err) {
      console.error('Failed to fetch actionable items:', err);
      setActionableItems([]);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    if (!activeCollection) return;

    try {
      const response = await fetch(
        getApiEndpoint(`/api/collections/${activeCollection.id}/notes/${noteId}`),
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete note');
      }

      await fetchNotes(activeCollection.id);
    } catch (err) {
      alert(`Failed to delete note: ${err.message}`);
    }
  };

  const handleDeleteActionItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this action item?')) return;
    if (!activeCollection) return;

    try {
      const response = await fetch(
        getApiEndpoint(`/api/collections/${activeCollection.id}/actionable-items/${itemId}`),
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete action item');
      }

      await fetchActionableItems(activeCollection.id);
    } catch (err) {
      alert(`Failed to delete action item: ${err.message}`);
    }
  };

  const handleUpdateActionItemStatus = async (itemId, newStatus) => {
    if (!activeCollection) return;

    try {
      const response = await fetch(
        getApiEndpoint(`/api/collections/${activeCollection.id}/actionable-items/${itemId}`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({ status: newStatus })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update action item');
      }

      await fetchActionableItems(activeCollection.id);
    } catch (err) {
      alert(`Failed to update action item: ${err.message}`);
    }
  };

  const handleCreateNote = async () => {
    if (!activeCollection) return;
    if (!newNoteTitle.trim() || !newNoteContent.trim()) {
      alert('Please provide both a title and content for the note.');
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${activeCollection.id}/notes`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: newNoteTitle.trim(),
          content: newNoteContent.trim()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create note');
      }

      await fetchNotes(activeCollection.id);
      setShowCreateNoteModal(false);
      setNewNoteTitle('');
      setNewNoteContent('');
      alert('Note created successfully!');
    } catch (err) {
      alert(`Failed to create note: ${err.message}`);
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNewNoteTitle(note.title);
    setNewNoteContent(note.content);
    setShowCreateNoteModal(true);
  };

  const handleUpdateNote = async () => {
    if (!activeCollection || !editingNote) return;
    if (!newNoteTitle.trim() || !newNoteContent.trim()) {
      alert('Please provide both a title and content for the note.');
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${activeCollection.id}/notes/${editingNote.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: newNoteTitle.trim(),
          content: newNoteContent.trim()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update note');
      }

      await fetchNotes(activeCollection.id);
      setShowCreateNoteModal(false);
      setEditingNote(null);
      setNewNoteTitle('');
      setNewNoteContent('');
      alert('Note updated successfully!');
    } catch (err) {
      alert(`Failed to update note: ${err.message}`);
    }
  };

  const handleCreateActionItem = async () => {
    if (!activeCollection) return;
    if (!newActionTitle.trim()) {
      alert('Please provide a title for the action item.');
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${activeCollection.id}/actionable-items`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: newActionTitle.trim(),
          description: newActionDescription.trim() || null,
          priority: newActionPriority,
          start_date: newActionStartDate || null,
          due_date: newActionDueDate || null,
          assignee_id: newActionAssignee || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create action item');
      }

      await fetchActionableItems(activeCollection.id);
      setShowCreateActionModal(false);
      setNewActionTitle('');
      setNewActionDescription('');
      setNewActionPriority('medium');
      setNewActionStartDate('');
      setNewActionDueDate('');
      setNewActionAssignee(null);
      alert('Action item created successfully!');
    } catch (err) {
      alert(`Failed to create action item: ${err.message}`);
    }
  };

  const handleEditActionItem = (item) => {
    setEditingActionItem(item);
    setNewActionTitle(item.title);
    setNewActionDescription(item.description || '');
    setNewActionPriority(item.priority);
    // Format dates for date input (YYYY-MM-DD)
    setNewActionStartDate(item.start_date ? new Date(item.start_date).toISOString().split('T')[0] : '');
    setNewActionDueDate(item.due_date ? new Date(item.due_date).toISOString().split('T')[0] : '');
    setNewActionAssignee(item.assignee_id || null);
    setShowCreateActionModal(true);
  };

  const handleUpdateActionItem = async () => {
    if (!activeCollection || !editingActionItem) return;
    if (!newActionTitle.trim()) {
      alert('Please provide a title for the action item.');
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${activeCollection.id}/actionable-items/${editingActionItem.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: newActionTitle.trim(),
          description: newActionDescription.trim() || null,
          priority: newActionPriority,
          start_date: newActionStartDate || null,
          due_date: newActionDueDate || null,
          assignee_id: newActionAssignee || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update action item');
      }

      await fetchActionableItems(activeCollection.id);
      setShowCreateActionModal(false);
      setEditingActionItem(null);
      setNewActionTitle('');
      setNewActionDescription('');
      setNewActionPriority('medium');
      setNewActionStartDate('');
      setNewActionDueDate('');
      setNewActionAssignee(null);
      alert('Action item updated successfully!');
    } catch (err) {
      alert(`Failed to update action item: ${err.message}`);
    }
  };

  const EXPORT_STYLES = [
    { key: 'bibtex',    label: 'BibTeX',     ext: '.bib' },
    { key: 'apa',       label: 'APA 7th',    ext: '.txt' },
    { key: 'mla',       label: 'MLA 9th',    ext: '.txt' },
    { key: 'chicago',   label: 'Chicago 17', ext: '.txt' },
    { key: 'vancouver', label: 'Vancouver',  ext: '.txt' },
    { key: 'ieee',      label: 'IEEE',       ext: '.txt' },
    { key: 'ris',       label: 'RIS',        ext: '.ris' },
  ];

  const handleExport = async (style) => {
    if (!activeCollection) return;
    setShowExportMenu(false);
    setExporting(true);
    try {
      const response = await fetch(
        getApiEndpoint(`/api/collections/${activeCollection.id}/export?style=${style}`),
        { headers: getAuthHeaders() }
      );
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers.get('Content-Disposition');
      const match = cd && cd.match(/filename="(.+)"/);
      const ext = EXPORT_STYLES.find(s => s.key === style)?.ext || '.txt';
      a.download = match ? match[1] : `${activeCollection.name.replace(/\s+/g, '_')}${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="collections-page">
      <div className={`collections-sidebar ${sidebarCollapsed ? 'collections-sidebar--collapsed' : ''}`}>
        <div className="collections-header">
          {!sidebarCollapsed && <h2>My Collections</h2>}
          <button className="collections-collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed ? 'Show collections' : 'Hide collections'}>
            {sidebarCollapsed ? <FiChevronDown style={{ transform: 'rotate(-90deg)' }} /> : <FiChevronUp style={{ transform: 'rotate(-90deg)' }} />}
          </button>
          {onBack && (
            <button
              className="back-to-search-button"
              onClick={onBack}
              style={{ marginBottom: '0.75rem', width: '100%' }}
            >
              ← Back to Search
            </button>
          )}
          <button
            className="create-collection-button"
            onClick={() => setShowCreateModal(true)}
          >
            <FiPlus /> New Collection
          </button>
          <button
            className="import-author-button"
            onClick={() => { setShowImportModal(true); setImportError(''); }}
            title="Import your publications from ORCID or by name"
          >
            ↓ Import My Publications
          </button>
        </div>

        {twinStats && (twinStats.papers_count > 0 || twinStats.proposals_count > 0) && (
          <div className="twin-depth-bar">
            <span className="twin-depth-stat" title="Papers ingested across all collections">
              📄 {twinStats.papers_count.toLocaleString()} {twinStats.papers_count === 1 ? 'paper' : 'papers'}
            </span>
            <span className="twin-depth-divider">·</span>
            <span className="twin-depth-stat" title="Grant proposals drafted">
              📋 {twinStats.proposals_count} {twinStats.proposals_count === 1 ? 'proposal' : 'proposals'}
            </span>
            <span className="twin-depth-divider">·</span>
            <span className="twin-depth-stat" title="Research ideas generated">
              💡 {twinStats.ideas_count} {twinStats.ideas_count === 1 ? 'idea' : 'ideas'}
            </span>
          </div>
        )}

        <div className="collections-list">
          {collections.length === 0 ? (
            <div className="no-collections">
              <p>No collections yet. Create one to get started!</p>
            </div>
          ) : (
            collections.map((collection) => (
              <div
                key={collection.id}
                className={`collection-item ${activeCollection && activeCollection.id === collection.id ? 'active' : ''}`}
                onClick={() => handleSelectCollection(collection)}
              >
                <div className="collection-item-content">
                  <FiFolder className="collection-icon" />
                  <div className="collection-info">
                    <h3>{collection.name}</h3>
                    <p className="collection-meta">
                      {collection.item_count} {collection.item_count === 1 ? 'item' : 'items'}
                    </p>
                  </div>
                </div>
                <button
                  className="delete-collection-button"
                  onClick={(e) => handleDeleteCollection(collection.id, e)}
                  title="Delete collection"
                >
                  <FiTrash2 />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="collections-content">
        {activeCollection ? (
          <>
            <div className="collection-header">
              <div className="collection-header-content">
                <div>
                  <div className="collection-name-row">
                    {isEditingName ? (
                      <input
                        className="collection-name-input"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameCollection();
                          if (e.key === 'Escape') setIsEditingName(false);
                        }}
                        onBlur={handleRenameCollection}
                        autoFocus
                      />
                    ) : (
                      <>
                        <h2>{activeCollection.name}</h2>
                        <button
                          className="edit-name-button"
                          onClick={() => { setEditedName(activeCollection.name); setIsEditingName(true); }}
                          title="Rename collection"
                        >
                          <FiEdit2 />
                        </button>
                      </>
                    )}
                  </div>
                  {activeCollection.description && (
                    <p className="collection-description">{activeCollection.description}</p>
                  )}
                  {activeCollection.team_id && (
                    <div className="collection-shared-badge">
                      <FiUsers style={{ marginRight: '0.5rem' }} />
                      <span>Shared with team: <strong>{teams.find(t => t.id === activeCollection.team_id)?.name || 'Team'}</strong></span>
                    </div>
                  )}
                </div>
                <div className="collection-header-actions">
                  {activeCollection.user_id && (
                    <button 
                      className="share-collection-button"
                      onClick={() => setShowShareModal(true)}
                      title="Share with Team"
                    >
                      <FiShare2 />
                      <span>Share</span>
                    </button>
                  )}
                  <button
                    className="lit-review-button"
                    onClick={() => setShowLitReviewModal(true)}
                    title="Generate AI literature review"
                  >
                    <FiBookOpen /> Lit Review
                  </button>
                  <div className="export-menu-wrapper" onMouseLeave={() => setShowExportMenu(false)}>
                    <button
                      className="export-bibtex-button"
                      onClick={() => setShowExportMenu(v => !v)}
                      title="Export collection references"
                      disabled={exporting}
                    >
                      <FiDownload /> {exporting ? 'Exporting…' : 'Export'} <FiChevronDown />
                    </button>
                    {showExportMenu && (
                      <div className="export-menu">
                        {EXPORT_STYLES.map(s => (
                          <button key={s.key} className="export-menu-item" onClick={() => handleExport(s.key)}>
                            <span className="export-menu-label">{s.label}</span>
                            <span className="export-menu-ext">{s.ext}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Workflow launch row */}
            {results && results.total > 0 && (
              <div className="collection-workflow-row">
                <span className="collection-workflow-label">Start with this collection:</span>
                <button
                  className="collection-workflow-btn"
                  onClick={() => onStartPaperAgent && onStartPaperAgent(activeCollection.id)}
                  title="Start a new research paper draft using papers from this collection"
                >
                  + New Paper Draft
                </button>
                <button
                  className="collection-workflow-btn"
                  onClick={() => onStartGrantAgent && onStartGrantAgent(activeCollection.id)}
                  title="Start a new grant proposal grounded in papers from this collection"
                >
                  + New Grant Proposal
                </button>
                <button
                  className="collection-workflow-btn collection-workflow-btn--secondary"
                  onClick={() => setActiveTab('collection-analysis')}
                  title="View research ideas from this collection"
                >
                  Research Ideas ↗
                </button>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="collection-tabs">
              <button
                className={`collection-tab ${activeTab === 'papers' ? 'active' : ''}`}
                onClick={() => setActiveTab('papers')}
              >
                <FiFolder /> Saved Papers
              </button>
              <button
                className={`collection-tab ${activeTab === 'context-docs' ? 'active' : ''}`}
                onClick={() => setActiveTab('context-docs')}
              >
                <FiFile /> My Documents ({contextDocuments.length})
              </button>
              <button
                className={`collection-tab ${activeTab === 'notes' ? 'active' : ''}`}
                onClick={() => setActiveTab('notes')}
              >
                <FiFileText /> Notes ({notes.length})
              </button>
              <button
                className={`collection-tab ${activeTab === 'actions' ? 'active' : ''}`}
                onClick={() => setActiveTab('actions')}
              >
                <FiCheckSquare /> Actions ({actionableItems.length})
              </button>
              <button
                className={`collection-tab ${activeTab === 'collection-analysis' ? 'active' : ''}`}
                onClick={() => setActiveTab('collection-analysis')}
              >
                <FiBarChart2 /> Ideas
              </button>
              <button
                className={`collection-tab ${activeTab === 'assistant' ? 'active' : ''}`}
                onClick={() => setActiveTab('assistant')}
              >
                <FiMessageCircle /> Ask Assistant
              </button>
            </div>

            {/* Tab Content */}
            <div className="collection-tab-content">
              {activeTab === 'papers' && (
                <div className="tab-panel">
            {loading && (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading collection...</p>
              </div>
            )}

            {error && (
              <div className="error-container">
                <p>❌ {error}</p>
              </div>
            )}

            {results && !loading && (
              <SearchResults
                results={results}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                perPage={perPage}
                searchQuery={`Collection: ${activeCollection.name}`}
                isCollectionView={true}
                collectionId={activeCollection.id}
                isAuthenticated={true}
                onCitationSearch={onCitationSearch}
                onRemoveFromCollection={handleRemoveFromCollection}
              />
            )}
                  {!results && !loading && (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <p>No papers in this collection yet.</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'context-docs' && (
                <div className="tab-panel">
                  <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>My Documents</h3>
                    <label className="file-upload-label">
                      <input
                        type="file"
                        accept=".pdf,.txt,.md"
                        onChange={handleUploadContextDocument}
                        disabled={uploadingDoc}
                        style={{ display: 'none' }}
                      />
                      <span className="file-upload-button">
                        <FiUpload /> {uploadingDoc ? 'Uploading...' : 'Upload Document'}
                      </span>
                    </label>
                  </div>
                  <p style={{ marginBottom: '0.5rem', color: '#666' }}>
                    Upload prior proposals, progress reports, white papers, or any supporting documents (PDF, TXT, or MD).
                    Every AI feature in this collection — idea generation, grant drafting, funder research — will read these alongside your papers.
                  </p>
                  <p style={{ marginBottom: '1rem', color: '#999', fontSize: '0.8rem' }}>
                    🔒 Files are stored securely on AWS infrastructure and never used to train AI models.{' '}
                    <a href="/dpa.html" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>Data Processing Agreement</a>
                  </p>
                  <div className="context-docs-list">
                    {contextDocuments.length === 0 ? (
                      <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: '2rem' }}>
                        No documents uploaded yet. Try uploading a prior proposal or progress report.
                      </p>
                    ) : (
                      contextDocuments.map((doc) => (
                        <div key={doc.id} className="context-doc-item">
                          <div className="context-doc-info">
                            <FiFile className="context-doc-icon" />
                            <div>
                              <strong>{doc.name}</strong>
                              <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                                {doc.filename} • {doc.file_type.toUpperCase()}
                              </p>
                            </div>
                          </div>
                          <button
                            className="download-context-doc-button"
                            onClick={() => handleDownloadContextDocument(doc)}
                            title="Download document"
                          >
                            <FiDownload />
                          </button>
                          <button
                            className="delete-context-doc-button"
                            onClick={() => handleDeleteContextDocument(doc.id)}
                            title="Delete document"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="tab-panel">
                  <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Notes</h3>
                    <button
                      className="modal-button primary"
                      onClick={() => {
                        setEditingNote(null);
                        setNewNoteTitle('');
                        setNewNoteContent('');
                        setShowCreateNoteModal(true);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <FiPlus /> New Note
                    </button>
                  </div>
                  <p style={{ marginBottom: '1rem', color: '#666' }}>
                    Notes saved from your chatbot discussions or created directly.
                  </p>
                  <div className="notes-list" style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
                    {notes.length === 0 ? (
                      <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: '2rem' }}>
                        No notes yet. Click "New Note" above to create one.
                      </p>
                    ) : (
                      notes.map((note) => (
                        <div key={note.id} className="note-item" style={{ 
                          background: '#f9f9f9', 
                          padding: '1rem', 
                          marginBottom: '1rem', 
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                            <div style={{ flex: 1 }}>
                              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>{note.title}</h3>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                className="edit-note-button"
                                onClick={() => handleEditNote(note)}
                                title="Edit note"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#667eea',
                                  cursor: 'pointer',
                                  padding: '0.25rem',
                                  fontSize: '1rem'
                                }}
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                className="delete-note-button"
                                onClick={() => handleDeleteNote(note.id)}
                                title="Delete note"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  padding: '0.25rem',
                                  fontSize: '1rem'
                                }}
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                          <p style={{ 
                            margin: '0 0 0.5rem 0', 
                            color: '#666', 
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.6'
                          }}>
                            {note.content}
                          </p>
                          <p style={{ 
                            margin: '0.5rem 0 0 0', 
                            fontSize: '0.85rem',
                            color: '#999'
                          }}>
                            Created by {note.username || 'Unknown'} • {new Date(note.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'actions' && (
                <div className="tab-panel">
                  <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Action Items</h3>
                    <button
                      className="modal-button primary"
                      onClick={() => {
                        setEditingActionItem(null);
                        setNewActionTitle('');
                        setNewActionDescription('');
                        setNewActionPriority('medium');
                        setNewActionStartDate('');
                        setNewActionDueDate('');
                        setNewActionAssignee(null);
                        setShowCreateActionModal(true);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <FiPlus /> New Action
                    </button>
                  </div>
                  <p style={{ marginBottom: '1rem', color: '#666' }}>
                    Trackable action items and tasks created from your discussions or directly.
                  </p>

                  {/* Filters and Sorting */}
                  {actionableItems.length > 0 && (
                    <div style={{ 
                      marginBottom: '1.5rem', 
                      padding: '1rem', 
                      background: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                        {/* Filter by Created By */}
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem', fontWeight: '500', color: '#666' }}>
                            Created By
                          </label>
                          <select
                            value={actionFilterCreatedBy}
                            onChange={(e) => setActionFilterCreatedBy(e.target.value)}
                            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                          >
                            <option value="all">All Creators</option>
                            {Array.from(new Set(actionableItems.map(item => item.user_id))).map(userId => {
                              const item = actionableItems.find(i => i.user_id === userId);
                              return <option key={userId} value={userId}>{item?.username || 'Unknown'}</option>;
                            })}
                          </select>
                        </div>

                        {/* Filter by Assigned To */}
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem', fontWeight: '500', color: '#666' }}>
                            Assigned To
                          </label>
                          <select
                            value={actionFilterAssignedTo}
                            onChange={(e) => setActionFilterAssignedTo(e.target.value)}
                            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                          >
                            <option value="all">All Assignees</option>
                            <option value="unassigned">Unassigned</option>
                            {Array.from(new Set(actionableItems.filter(item => item.assignee_id).map(item => item.assignee_id))).map(assigneeId => {
                              const item = actionableItems.find(i => i.assignee_id === assigneeId);
                              return <option key={assigneeId} value={assigneeId}>{item?.assignee_username || 'Unknown'}</option>;
                            })}
                          </select>
                        </div>

                        {/* Filter by Status */}
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem', fontWeight: '500', color: '#666' }}>
                            Status
                          </label>
                          <select
                            value={actionFilterStatus}
                            onChange={(e) => setActionFilterStatus(e.target.value)}
                            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                          >
                            <option value="all">All Statuses</option>
                            <option value="todo">To Do</option>
                            <option value="in_progress">In Progress</option>
                            <option value="done">Done</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>

                        {/* Sort By */}
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem', fontWeight: '500', color: '#666' }}>
                            Sort By
                          </label>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <select
                              value={actionSortBy}
                              onChange={(e) => setActionSortBy(e.target.value)}
                              style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                            >
                              <option value="updated">Last Updated</option>
                              <option value="start_date">Start Date</option>
                              <option value="due_date">Due Date</option>
                            </select>
                            <button
                              onClick={() => setActionSortOrder(actionSortOrder === 'asc' ? 'desc' : 'asc')}
                              style={{
                                padding: '0.5rem 0.75rem',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                background: 'white',
                                cursor: 'pointer',
                                fontSize: '1rem'
                              }}
                              title={actionSortOrder === 'asc' ? 'Sort Descending' : 'Sort Ascending'}
                            >
                              {actionSortOrder === 'asc' ? '↑' : '↓'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="action-items-list" style={{ maxHeight: 'calc(100vh - 500px)', overflowY: 'auto' }}>
                    {(() => {
                      // Filter and sort action items
                      let filteredItems = [...actionableItems];

                      // Apply filters
                      if (actionFilterCreatedBy !== 'all') {
                        filteredItems = filteredItems.filter(item => item.user_id === parseInt(actionFilterCreatedBy));
                      }
                      if (actionFilterAssignedTo !== 'all') {
                        if (actionFilterAssignedTo === 'unassigned') {
                          filteredItems = filteredItems.filter(item => !item.assignee_id);
                        } else {
                          filteredItems = filteredItems.filter(item => item.assignee_id === parseInt(actionFilterAssignedTo));
                        }
                      }
                      if (actionFilterStatus !== 'all') {
                        filteredItems = filteredItems.filter(item => item.status === actionFilterStatus);
                      }

                      // Apply sorting
                      filteredItems.sort((a, b) => {
                        let aValue, bValue;
                        
                        if (actionSortBy === 'start_date') {
                          aValue = a.start_date ? new Date(a.start_date).getTime() : 0;
                          bValue = b.start_date ? new Date(b.start_date).getTime() : 0;
                        } else if (actionSortBy === 'due_date') {
                          aValue = a.due_date ? new Date(a.due_date).getTime() : 0;
                          bValue = b.due_date ? new Date(b.due_date).getTime() : 0;
                        } else { // updated
                          aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
                          bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
                        }

                        if (actionSortOrder === 'asc') {
                          return aValue - bValue;
                        } else {
                          return bValue - aValue;
                        }
                      });

                      // Render items
                      if (filteredItems.length === 0) {
                        return (
                          <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: '2rem' }}>
                            {actionableItems.length === 0 
                              ? 'No action items yet. Click "New Action" above to create one.'
                              : 'No action items match the selected filters.'}
                          </p>
                        );
                      }

                      return filteredItems.map((item) => (
                        <div key={item.id} className="action-item" style={{ 
                          background: '#f9f9f9', 
                          padding: '1rem', 
                          marginBottom: '1rem', 
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}>
                          {/* Header with title, priority, and action buttons */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>{item.title}</h3>
                                <span style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  background: item.priority === 'high' ? '#fee2e2' : item.priority === 'medium' ? '#fef3c7' : '#e0e7ff',
                                  color: item.priority === 'high' ? '#991b1b' : item.priority === 'medium' ? '#92400e' : '#3730a3'
                                }}>
                                  {item.priority.toUpperCase()}
                                </span>
                              </div>
                              {item.description && (
                                <p style={{ margin: '0 0 0.75rem 0', color: '#666', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                                  {item.description}
                                </p>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                className="edit-action-button"
                                onClick={() => handleEditActionItem(item)}
                                title="Edit action item"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#667eea',
                                  cursor: 'pointer',
                                  padding: '0.25rem',
                                  fontSize: '1rem'
                                }}
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                className="delete-action-button"
                                onClick={() => handleDeleteActionItem(item.id)}
                                title="Delete action item"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  padding: '0.25rem',
                                  fontSize: '1rem'
                                }}
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>

                          {/* Information grid: Start Date, Assigned To, Due Date, Status */}
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                            gap: '1rem',
                            padding: '0.75rem',
                            background: 'white',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            marginBottom: '0.75rem'
                          }}>
                            {/* Start Date */}
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#999', fontWeight: '500', marginBottom: '0.25rem' }}>
                                START DATE
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500' }}>
                                {item.start_date ? new Date(item.start_date).toLocaleDateString() : 'Not set'}
                              </div>
                            </div>

                            {/* Assigned To */}
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#999', fontWeight: '500', marginBottom: '0.25rem' }}>
                                ASSIGNED TO
                              </div>
                              <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                                {item.assignee_username ? (
                                  <div>
                                    <div style={{ color: '#667eea' }}>{item.assignee_username}</div>
                                    {item.assignee_email && (
                                      <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.1rem' }}>
                                        {item.assignee_email}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: '#999' }}>Unassigned</span>
                                )}
                              </div>
                            </div>

                            {/* Due Date */}
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#999', fontWeight: '500', marginBottom: '0.25rem' }}>
                                DUE DATE
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500' }}>
                                {item.due_date ? new Date(item.due_date).toLocaleDateString() : 'Not set'}
                              </div>
                            </div>

                            {/* Status */}
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#999', fontWeight: '500', marginBottom: '0.25rem' }}>
                                STATUS
                              </div>
                              <select
                                value={item.status}
                                onChange={(e) => handleUpdateActionItemStatus(item.id, e.target.value)}
                                style={{
                                  padding: '0.5rem',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  fontSize: '0.85rem',
                                  background: 'white',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  width: '100%'
                                }}
                              >
                                <option value="todo">To Do</option>
                                <option value="in_progress">In Progress</option>
                                <option value="done">Done</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </div>
                          </div>

                          {/* Footer: Created by and timestamp */}
                          <div style={{ fontSize: '0.85rem', color: '#999', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Created by {item.username || 'Unknown'}</span>
                            <span>{new Date(item.updated_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {activeTab === 'collection-analysis' && (
                <div className="tab-panel assistant-panel">
                  <div className="analysis-container">
                    {showGuide && ideas.length > 0 && (
                      <div className="ideas-guide-banner">
                        <h4>Welcome — we analyzed your papers and found {ideas.length} research directions.</h4>
                        <ol>
                          <li>Browse the ideas below — each identifies a gap where you have an advantage</li>
                          <li>Click "Start a Paper Draft" on any idea to generate a draft with real citations</li>
                          <li>Run Panel Review — 6 reviewers flag issues before submission</li>
                          <li>Click "Fix Issues" to auto-revise, then re-review until ready</li>
                        </ol>
                        <div className="ideas-guide-banner-actions">
                          <button onClick={() => {
                            document.querySelector('.ideas-card--top-pick')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            markGuideSeen(user?.id, 'ideas_guide');
                            setShowGuide(false);
                          }}>Start with top pick &rarr;</button>
                          <button onClick={() => {
                            markGuideSeen(user?.id, 'ideas_guide');
                            setShowGuide(false);
                          }}>Dismiss &#10005;</button>
                        </div>
                      </div>
                    )}
                    {/* Research Ideas section */}
                    <div className="ideas-section">
                      <div className="analysis-header ideas-section-header section-header-collapsible" onClick={() => togglePanel('ideasSection')}>
                        <h3>
                          {expandedPanels.ideasSection ? <FiChevronDown /> : <FiChevronRight />}
                          {' '}Research Ideas
                          {ideas.length > 0 && <span className="section-badge">{ideas.length} ideas</span>}
                        </h3>
                        <button
                          className="generate-analysis-btn"
                          onClick={() => fetchIdeas(activeCollection.id, true)}
                          disabled={ideasLoading}
                        >
                          {ideasLoading ? 'Generating...' : ideas.length > 0 ? 'Regenerate Ideas' : 'Generate Ideas'}
                        </button>
                      </div>

                      {expandedPanels.ideasSection && ideasLoading && (
                        <div className="analysis-loading">
                          <div className="spinner"></div>
                          <p>Identifying research opportunities grounded in your papers...</p>
                        </div>
                      )}

                      {expandedPanels.ideasSection && !ideasLoading && ideas.length > 0 && ideasContext && (ideasContext.collections_count > 0 || ideasContext.proposals_count > 0) && (
                        <div className="ideas-context-banner">
                          <span className="ideas-context-icon">🧠</span>
                          <span className="ideas-context-text">
                            Generated using context from{' '}
                            {ideasContext.collections_count > 0 && (
                              <strong title={ideasContext.collection_names.join(', ')}>
                                {ideasContext.collections_count} other collection{ideasContext.collections_count !== 1 ? 's' : ''}
                              </strong>
                            )}
                            {ideasContext.collections_count > 0 && ideasContext.proposals_count > 0 && ' and '}
                            {ideasContext.proposals_count > 0 && (
                              <strong title={ideasContext.proposal_titles.join(', ')}>
                                {ideasContext.proposals_count} prior proposal{ideasContext.proposals_count !== 1 ? 's' : ''}
                              </strong>
                            )}
                            {' '}— ideas reflect your full research trajectory, not just this collection.
                          </span>
                        </div>
                      )}

                      {expandedPanels.ideasSection && !ideasLoading && ideas.length > 0 && (() => {
                        // Sort by convergence score: expertise_depth / log(novelty_count + 2)
                        const scored = ideas.map((idea) => ({
                          ...idea,
                          _score: (idea.expertise_depth || 0) / Math.log((idea.novelty_count || 0) + 2),
                        }));
                        const topPick = scored.reduce((best, cur) => cur._score > best._score ? cur : best, scored[0]);
                        const rest = scored.filter((idea) => idea.id !== topPick.id);

                        const renderIdeaCard = (idea, ideaIdx, isTopPick = false) => (
                          <div key={idea.id} className={`ideas-card${isTopPick ? ' ideas-card--top-pick' : ''}`}>
                            {isTopPick && (
                              <div className="ideas-top-pick-label">Top Pick for You</div>
                            )}
                            <div className="ideas-card-top">
                              <span className="ideas-card-title">💡 {idea.title}</span>
                              <div className="ideas-card-badges">
                                <span className={`ideas-question-badge ideas-question-badge--${idea.question_type}`}>
                                  {idea.question_type}
                                </span>
                                {idea.novelty_count != null && (
                                  <span
                                    className={`ideas-novelty-badge ideas-novelty-badge--${idea.novelty_count < 10 ? 'sparse' : idea.novelty_count < 100 ? 'moderate' : 'crowded'}`}
                                    title={`~${idea.novelty_count.toLocaleString()} papers on OpenAlex`}
                                  >
                                    {idea.novelty_count < 10 ? '🟢 sparse' : idea.novelty_count < 100 ? '🟡 emerging' : '🔴 crowded'}{' '}
                                    ~{idea.novelty_count >= 1000 ? `${Math.round(idea.novelty_count / 1000)}k` : idea.novelty_count}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Convergence signal */}
                            {(idea.expertise_depth != null || idea.novelty_count != null) && (
                              <div className="ideas-convergence">
                                {idea.expertise_depth > 0 && (
                                  <span className="ideas-convergence-yours" title="Papers in your collections matching this topic">
                                    Your papers: {idea.expertise_depth}
                                  </span>
                                )}
                                {idea.expertise_depth > 0 && idea.novelty_count != null && (
                                  <span className="ideas-convergence-sep"> / </span>
                                )}
                                {idea.novelty_count != null && (
                                  <span className="ideas-convergence-global" title="Total papers on OpenAlex">
                                    Global: {idea.novelty_count >= 1000 ? `~${Math.round(idea.novelty_count / 1000)}k` : idea.novelty_count}
                                  </span>
                                )}
                                {idea.expertise_depth > 0 && idea.novelty_count != null && idea.novelty_count < 50 && (
                                  <span className="ideas-convergence-signal"> — you own a significant share of this space</span>
                                )}
                              </div>
                            )}

                            {idea.why_you && (
                              <div className="ideas-why-you">{idea.why_you}</div>
                            )}
                            {idea.builds_on && (
                              <div className="ideas-builds-on">🔗 {idea.builds_on}</div>
                            )}
                            <p className="ideas-card-gap">{idea.gap}</p>
                            <p className="ideas-card-description">{idea.description}</p>
                            {idea.supporting_papers && idea.supporting_papers.length > 0 && (
                              <div className="ideas-papers-row">
                                <span className="ideas-papers-label">Grounded in:</span>
                                {idea.supporting_papers.map((paper, paperIdx) => {
                                  const chipKey = `${ideaIdx}_${paperIdx}`;
                                  const isExpanded = !!expandedPapers[chipKey];
                                  return (
                                    <div key={paperIdx} className="ideas-paper-chip-wrapper">
                                      <button
                                        className={`ideas-paper-chip ${isExpanded ? 'ideas-paper-chip--expanded' : ''}`}
                                        onClick={() => togglePaperChip(ideaIdx, paperIdx)}
                                        title="Click to see paper details"
                                      >
                                        {paper.citation_key}
                                      </button>
                                      {isExpanded && (
                                        <div className="ideas-paper-detail">
                                          <strong>{paper.title}</strong>
                                          {paper.authors && paper.authors.length > 0 && (
                                            <span className="ideas-paper-authors"> — {paper.authors.slice(0, 3).join(', ')}{paper.authors.length > 3 ? ' et al.' : ''} ({paper.year})</span>
                                          )}
                                          {paper.abstract && (
                                            <p className="ideas-paper-abstract">{paper.abstract.slice(0, 300)}{paper.abstract.length > 300 ? '...' : ''}</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className="ideas-actions">
                              <button
                                className="ideas-action-btn"
                                onClick={async () => {
                                  logIdeaInteraction(activeCollection.id, idea, 'pursued_paper');
                                  // Create a writing collection from the idea's supporting papers
                                  try {
                                    const res = await fetch(
                                      getApiEndpoint(`/api/collections/${activeCollection.id}/create-writing-collection`),
                                      {
                                        method: 'POST',
                                        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          idea_title: idea.title,
                                          supporting_papers: (idea.supporting_papers || []).map(p => ({
                                            citation_key: p.citation_key,
                                            title: p.title,
                                          })),
                                        }),
                                      }
                                    );
                                    if (res.ok) {
                                      const data = await res.json();
                                      onStartPaperAgent && onStartPaperAgent(data.collection_id, idea);
                                    } else {
                                      // Fallback: use the source collection
                                      onStartPaperAgent && onStartPaperAgent(activeCollection.id, idea);
                                    }
                                  } catch (_) {
                                    onStartPaperAgent && onStartPaperAgent(activeCollection.id, idea);
                                  }
                                }}
                              >
                                + Start a Paper Draft
                              </button>
                            </div>
                          </div>
                        );

                        return (
                          <div className="ideas-list">
                            {renderIdeaCard(topPick, 0, true)}
                            {rest.map((idea, i) => renderIdeaCard(idea, i + 1, false))}
                          </div>
                        );
                      })()}

                      {expandedPanels.ideasSection && !ideasLoading && ideas.length === 0 && (
                        <div className="analysis-empty">
                          <p>Click "Generate Ideas" to discover research opportunities grounded in your specific papers.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              )}

              {activeTab === 'collection-analysis' && activeCollection && (
                <div className="analysis-section">
                  <div className="analysis-section-header">
                    <h3>Recommended Papers</h3>
                    <button className="generate-analysis-btn" onClick={() => fetchRecommendations(activeCollection.id)} disabled={recsLoading}>
                      {recsLoading ? 'Finding...' : recommendations ? 'Refresh' : 'Find Similar Papers'}
                    </button>
                  </div>
                  {recsLoading && <div className="analysis-loading"><div className="spinner" /> Finding papers you should read...</div>}
                  {recommendations && recommendations.recommendations && (
                    <div className="recs-panel">
                      {recommendations.concepts_used && <p className="recs-concepts">Based on: {recommendations.concepts_used.join(', ')}</p>}
                      {recommendations.recommendations.length === 0
                        ? <p className="analysis-empty-text">No new papers found</p>
                        : <div className="recs-list">
                            {recommendations.recommendations.map((rec, i) => (
                              <div key={i} className="recs-card">
                                <div className="recs-card-main">
                                  <span className="recs-title">{rec.title}</span>
                                  <span className="recs-meta">
                                    {(rec.authors || []).slice(0, 2).join(', ')} {rec.year ? `(${rec.year})` : ''}
                                    {rec.source ? ` — ${rec.source}` : ''}
                                    {rec.cited_by_count > 0 ? ` · ${rec.cited_by_count} citations` : ''}
                                  </span>
                                </div>
                                <button className="recs-add-btn" onClick={() => addRecommendedPaper(activeCollection.id, rec.id)}>+ Add</button>
                              </div>
                            ))}
                          </div>
                      }
                    </div>
                  )}
                  {!recsLoading && !recommendations && (
                    <div className="analysis-empty"><p>Find highly-cited papers you might be missing based on your collection themes.</p></div>
                  )}
                </div>
              )}

              {activeTab === 'assistant' && (
                <div className="tab-panel assistant-panel">
                  <Chatbot
                    collectionId={activeCollection.id}
                    collectionName={activeCollection.name}
                    collectionAnalysis={null}
                    isOpen={true}
                    onClose={() => setActiveTab('papers')}
                    onBackToSearch={onBack ? () => {
                      setActiveTab('papers');
                      onBack();
                    } : undefined}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="no-active-collection">
            <FiFolder className="empty-icon" />
            <h2>No Active Collection</h2>
            <p>Select a collection from the sidebar or create a new one to view works.</p>
            <button
              className="create-collection-button"
              onClick={() => setShowCreateModal(true)}
            >
              <FiPlus /> Create Collection
            </button>
          </div>
        )}
      </div>

      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowImportModal(false)}><FiX /></button>
            <h2>Import My Publications</h2>
            <p style={{ color: '#6b7280', fontSize: '13.5px', marginBottom: '1.2rem', lineHeight: '1.6' }}>
              Enter your ORCID, full name, or Google Scholar profile name. Mnevia will find your publications on OpenAlex and create a collection automatically.
            </p>
            <div className="form-group">
              <label>ORCID or Name</label>
              <input
                type="text"
                value={importQuery}
                onChange={(e) => setImportQuery(e.target.value)}
                placeholder="e.g. 0000-0001-2345-6789 or Jane Smith"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && !importLoading && importAuthorPapers()}
              />
            </div>
            {importError && (
              <p style={{ color: '#dc2626', fontSize: '13px', marginBottom: '0.8rem' }}>{importError}</p>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowImportModal(false)}>Cancel</button>
              <button
                type="button"
                className="create-btn"
                onClick={importAuthorPapers}
                disabled={importLoading || !importQuery.trim()}
              >
                {importLoading ? 'Importing…' : 'Import Publications'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCreateModal(false)}>
              <FiX />
            </button>
            <h2>Create New Collection</h2>
            <form onSubmit={handleCreateCollection}>
              <div className="form-group">
                <label>Collection Name *</label>
                <input
                  type="text"
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  placeholder="e.g., My Research Papers"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={newCollectionDesc}
                  onChange={(e) => setNewCollectionDesc(e.target.value)}
                  placeholder="Add a description..."
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Create/Edit Note Modal */}
      {showCreateNoteModal && activeCollection && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateNoteModal(false);
          setEditingNote(null);
          setNewNoteTitle('');
          setNewNoteContent('');
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <button className="modal-close" onClick={() => {
              setShowCreateNoteModal(false);
              setEditingNote(null);
              setNewNoteTitle('');
              setNewNoteContent('');
            }}>
              <FiX />
            </button>
            <h2>{editingNote ? 'Edit Note' : 'Create Note'}</h2>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              {editingNote ? 'Update your note.' : 'Save important insights or knowledge.'}
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Title *
              </label>
              <input
                type="text"
                className="modal-input"
                value={newNoteTitle}
                onChange={(e) => setNewNoteTitle(e.target.value)}
                placeholder="Note title..."
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Content *
              </label>
              <textarea
                className="modal-textarea"
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Note content..."
                rows="6"
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="modal-button secondary" onClick={() => {
                setShowCreateNoteModal(false);
                setEditingNote(null);
                setNewNoteTitle('');
                setNewNoteContent('');
              }}>
                Cancel
              </button>
              <button className="modal-button primary" onClick={editingNote ? handleUpdateNote : handleCreateNote}>
                {editingNote ? 'Update Note' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Action Item Modal */}
      {showCreateActionModal && activeCollection && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateActionModal(false);
          setEditingActionItem(null);
          setNewActionTitle('');
          setNewActionDescription('');
          setNewActionPriority('medium');
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <button className="modal-close" onClick={() => {
              setShowCreateActionModal(false);
              setEditingActionItem(null);
              setNewActionTitle('');
              setNewActionDescription('');
              setNewActionPriority('medium');
            }}>
              <FiX />
            </button>
            <h2>{editingActionItem ? 'Edit Action Item' : 'Create Action Item'}</h2>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              {editingActionItem ? 'Update your action item or task.' : 'Create a trackable action item or task.'}
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Title *
              </label>
              <input
                type="text"
                className="modal-input"
                value={newActionTitle}
                onChange={(e) => setNewActionTitle(e.target.value)}
                placeholder="Action item title..."
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                className="modal-textarea"
                value={newActionDescription}
                onChange={(e) => setNewActionDescription(e.target.value)}
                placeholder="Action item description..."
                rows="4"
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Priority
              </label>
              <select
                value={newActionPriority}
                onChange={(e) => setNewActionPriority(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div style={{ marginBottom: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={newActionStartDate}
                  onChange={(e) => setNewActionStartDate(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Due Date
                </label>
                <input
                  type="date"
                  value={newActionDueDate}
                  onChange={(e) => setNewActionDueDate(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            </div>
            {collectionTeamId && teamMembers.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Assign To (Optional)
                </label>
                <select
                  value={newActionAssignee || ''}
                  onChange={(e) => setNewActionAssignee(e.target.value ? parseInt(e.target.value) : null)}
                  style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.user_id}>
                      {member.username} ({member.email}) {member.role === 'owner' ? '- Owner' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="modal-button secondary" onClick={() => {
                setShowCreateActionModal(false);
                setEditingActionItem(null);
                setNewActionTitle('');
                setNewActionDescription('');
                setNewActionPriority('medium');
                setNewActionStartDate('');
                setNewActionDueDate('');
                setNewActionAssignee(null);
              }}>
                Cancel
              </button>
              <button className="modal-button primary" onClick={editingActionItem ? handleUpdateActionItem : handleCreateActionItem}>
                {editingActionItem ? 'Update Action Item' : 'Create Action Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Collection Modal */}
      {showShareModal && activeCollection && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowShareModal(false)}>
              <FiX />
            </button>
            <h2>Share Collection with Team</h2>
            {collectionTeamId ? (
              <div>
                <p>This collection is currently shared with a team.</p>
                <button 
                  className="unshare-button"
                  onClick={handleUnshareFromTeam}
                >
                  Unshare from Team
                </button>
              </div>
            ) : (
              <div>
                <p>Select a team to share this collection with:</p>
                {teams.length === 0 ? (
                  <p className="no-teams-message">You don't have any teams yet. Create a team first.</p>
                ) : (
                  <div className="teams-list-share">
                    {teams.map((team) => (
                      <div
                        key={team.id}
                        className="team-share-item"
                        onClick={() => handleShareWithTeam(team.id)}
                      >
                        <FiUsers />
                        <div>
                          <div className="team-share-name">{team.name}</div>
                          <div className="team-share-meta">{team.member_count} members</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowShareModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <LitReviewModal
        isOpen={showLitReviewModal}
        onClose={() => setShowLitReviewModal(false)}
        collection={activeCollection}
      />
    </div>
  );
};

export default Collections;

